import React, { useState, useEffect } from 'react';
import { Button, Form } from 'react-bootstrap';
import { TfiAnnouncement } from 'react-icons/tfi';
import { BsSearch, BsChevronLeft, BsChevronRight } from 'react-icons/bs';
import axios from 'axios';
import './anouncement.css';
import { IoIosCloseCircle } from "react-icons/io";
import { useLocation } from 'react-router-dom';

const formatDateTime = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  if (isNaN(date)) return 'Invalid Date';
  return new Intl.DateTimeFormat('en-US', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
};

// Loading Animation
function LoadingAnimation({ isVisible, message }) {
  if (!isVisible) return null;

  return (
    <div className="jk-loading-overlay">
      <div className="jk-loading-content">
        <div className="jk-loading-animation">
          <div className="jk-loading-spinner"></div>
          <div className="jk-loading-progress-bar">
            <div className="jk-progress-fill"></div>
          </div>
          <div className="jk-particle-container">
            {[...Array(10)].map((_, i) => (
              <div key={i} className="jk-particle"></div>
            ))}
          </div>
          <div className="jk-loading-checkmark">Checkmark</div>
        </div>
        <p className="jk-loading-message">{message}</p>
      </div>
    </div>
  );
}

// Slide-out Panel Component for Announcement Details
function AnnouncementDetailPanel({ announcement, isOpen, onClose, userId, isUnread }) {
  useEffect(() => {
    if (isOpen && announcement && userId && isUnread) {
      markAnnouncementAsSeen(announcement._id, userId, announcement.source);
    }
  }, [isOpen, announcement, userId, isUnread]);

  const markAnnouncementAsSeen = async (announcementId, userId, source) => {
    try {
      let endpoint = '';
      if (announcement.type === 'regular') {
        endpoint = source === 'role'
          ? `https://backend.ems.hiradentallab.com/api/announcements/announcements/${announcementId}/mark-seen`
          : `https://backend.ems.hiradentallab.com/api/user/announcements/${announcementId}/mark-seen`;
      } else {
        endpoint = `https://backend.ems.hiradentallab.com/api/deptannouncements/${announcementId}/mark-seen`;
      }

      await axios.post(endpoint, { userId });
    } catch (error) {
      console.error('Error marking announcement as seen:', error);
    }
  };

  if (!announcement) return null;

  // Fallback: content → message
  const displayMessage = announcement.message || announcement.content || "No description available";

  const renderFile = (file) => {
    const fileUrl = file.fileUrl.startsWith('http')
      ? file.fileUrl
      : `https://backend.ems.hiradentallab.com${file.fileUrl}`;

    switch (file.fileType) {
      case 'application/pdf':
        return (
          <div key={file._id} className="jk-file-item">
            <a href={fileUrl} target="_blank" rel="noopener noreferrer" className="jk-file-link">
              {file.fileName} (Open in new tab)
            </a>
            <iframe
              src={fileUrl}
              title={file.fileName}
              style={{ width: '100%', height: '500px', marginTop: '10px', border: '1px solid #eaeaea', borderRadius: '8px' }}
            />
          </div>
        );
      case 'image/jpeg':
      case 'image/png':
      case 'image/gif':
        return (
          <div key={file._id} className="jk-file-item">
            <a href={fileUrl} target="_blank" rel="noopener noreferrer" className="jk-file-link">
              {file.fileName} (Open in new tab)
            </a>
            <img
              src={fileUrl}
              alt={file.fileName}
              style={{ maxWidth: '100%', maxHeight: '500px', marginTop: '10px', borderRadius: '8px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)' }}
            />
          </div>
        );
      case 'application/msword':
      case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
        return (
          <div key={file._id} className="jk-file-item">
            <a href={fileUrl} target="_blank" rel="noopener noreferrer" className="jk-file-link">
              {file.fileName} (Download Word document)
            </a>
          </div>
        );
      default:
        return (
          <div key={file._id} className="jk-file-item">
            <a href={fileUrl} target="_blank" rel="noopener noreferrer" className="jk-file-link">
              {file.fileName} (Download file)
            </a>
          </div>
        );
    }
  };

  let typeLabel = '';
  if (announcement.type === 'department') {
    typeLabel = `Department Announcement: ${announcement.department}`;
  } else if (announcement.source === 'user') {
    typeLabel = 'User Announcement: Personal';
  } else {
    typeLabel = `Role Announcement: ${announcement.category || 'Personal'}`;
  }

  return (
    <div className={`jk-detail-panel ${isOpen ? 'open' : ''}`}>
      <div className="jk-panel-header">
        <Button variant="link" className="jk-close-panel" onClick={onClose}>
          <IoIosCloseCircle style={{ color: 'red' }} />
        </Button>
        <h3>Announcement Details</h3>
      </div>

      <div className="jk-panel-content">
        <div className="jk-announcement-detail-header">
          <div className="jk-announcement-detail-info">
            <div className="jk-detail-field">
              <strong>Title:</strong> {announcement.title}
            </div>
            <p className="jk-announcement-meta">
              <strong>Date:</strong> {formatDateTime(announcement.createdAt)} •
              <strong>Type:</strong> {typeLabel}
            </p>
          </div>
        </div>

        {/* DESCRIPTION: CLEAN, NO BORDER, NO BG */}
        <div className="jk-announcement-content">
          <div className="jk-detail-field">
            <strong>Description:</strong>
            <p style={{
              margin: '10px 0 0',
              padding: 0,
              fontSize: '15px',
              color: '#212529',
              whiteSpace: 'pre-wrap',
              lineHeight: '1.6',
              fontFamily: 'inherit'
            }}>
              {displayMessage}
            </p>
          </div>
        </div>

        <div className="jk-announcement-footer">
          <div className="jk-detail-field">
            <strong>Author:</strong> <span style={{ color: '#007bff', fontWeight: 'bold' }}>{announcement.author || "Admin"}</span>
          </div>
          {announcement.type === 'regular' && announcement.category && (
            <div className="jk-detail-field">
              <strong>Category:</strong> <span style={{ color: '#007bff', fontWeight: 'bold' }}>{announcement.category}</span>
            </div>
          )}
          {announcement.type === 'regular' && announcement.role && (
            <div className="jk-detail-field">
              <strong>Role:</strong> <span style={{ color: '#007bff', fontWeight: 'bold' }}>{announcement.role}</span>
            </div>
          )}
          {announcement.type === 'department' && announcement.department && (
            <div className="jk-detail-field">
              <strong>Department:</strong> <span style={{ color: '#007bff', fontWeight: 'bold' }}>{announcement.department}</span>
            </div>
          )}
          {announcement.files && announcement.files.length > 0 && (
            <div className="jk-announcement-files">
              <div className="jk-detail-field">
                <strong>Attached Files:</strong>
              </div>
              <div className="jk-file-list">
                {announcement.files.map(file => renderFile(file))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Announcement Item with Preview
function AnnouncementItem({ announcement, onClick, isUnread }) {
  const previewText = (announcement.message || announcement.content || "").substring(0, 80) + (announcement.message || announcement.content ? "..." : "");

  return (
    <div
      className={`jk-announcement-item ${isUnread ? 'unread' : ''}`}
      onClick={() => onClick(announcement)}
    >
      <div className="jk-announcement-content-container">
        <div className="jk-announcement-item-header">
          <h5 className="jk-announcement-title">
            {isUnread && <span className="jk-unread-badge"></span>}
            {announcement.title}
          </h5>
          <span className="jk-announcement-time">
            {formatDateTime(announcement.createdAt)}
          </span>
        </div>
        {previewText && (
          <p className="jk-announcement-preview" style={{ margin: '8px 0 0', color: '#555', fontSize: '14px' }}>
            {previewText}
          </p>
        )}
      </div>
    </div>
  );
}

export default function Announcement() {
  const location = useLocation();
  const [rowsPerPage] = useState(15);
  const [currentPage, setCurrentPage] = useState(1);
  const [regularData, setRegularData] = useState([]);
  const [deptData, setDeptData] = useState([]);
  const [combinedData, setCombinedData] = useState([]);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredData, setFilteredData] = useState([]);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const userId = parseInt(localStorage.getItem('userId'));
  const userRole = localStorage.getItem('role') || 'All';
  const userDepartment = localStorage.getItem('department');

  const fetchAllAnnouncements = async () => {
    let uniqueRegular = [];
    let deptAnnouncements = [];

    try {
      const regularRoleResponse = await axios.get('https://backend.ems.hiradentallab.com/api/announcements/role/announcement', {
        params: { role: userRole, userId: userId }
      });
      const regularUserResponse = await axios.get(`https://backend.ems.hiradentallab.com/api/user/announcements/user/${userId}`);

      const allRegular = [
        ...(regularRoleResponse.data || []).map(item => ({ ...item, type: 'regular', source: 'role' })),
        ...(regularUserResponse.data || []).map(item => ({ ...item, type: 'regular', source: 'user' }))
      ];
      uniqueRegular = allRegular.filter((item, index, self) =>
        self.findIndex(t => t._id === item._id) === index
      );
    } catch (error) {
      console.error('Error fetching regular announcements:', error);
    }

    if (userDepartment) {
      try {
        const deptResponse = await axios.get('https://backend.ems.hiradentallab.com/api/deptannouncements/by-department', {
          params: { department: userDepartment, userId: userId }
        });
        deptAnnouncements = deptResponse.data || [];
      } catch (error) {
        console.error('Error fetching department announcements:', error);
      }
    }

    setRegularData(uniqueRegular);
    setDeptData(deptAnnouncements);

    const combined = [
      ...uniqueRegular,
      ...deptAnnouncements.map(item => ({ ...item, type: 'department', source: 'department' }))
    ].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

    setCombinedData(combined);
  };

  useEffect(() => {
    if (userId && userRole) {
      fetchAllAnnouncements();
    }
  }, [userId, userRole]);

  // Handle selected announcement from navigation state (from dialog)
  useEffect(() => {
    const selectedAnnouncementFromState = location.state?.selectedAnnouncement;
    
    if (selectedAnnouncementFromState) {
      setSelectedAnnouncement(selectedAnnouncementFromState);
      setIsPanelOpen(true);
      // Clear the state to prevent reopening on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const applyFilters = (data, query) => {
    let filtered = data;
    if (query) {
      filtered = filtered.filter(item => {
        const title = (item.title || "").toLowerCase();
        const content = (item.message || item.content || "").toLowerCase();
        const category = (item.category || "").toLowerCase();
        const department = (item.department || "").toLowerCase();
        const author = (item.author || "").toLowerCase();

        return (
          title.includes(query.toLowerCase()) ||
          content.includes(query.toLowerCase()) ||
          category.includes(query.toLowerCase()) ||
          department.includes(query.toLowerCase()) ||
          author.includes(query.toLowerCase())
        );
      });
    }
    setFilteredData(filtered);
  };

  useEffect(() => {
    applyFilters(combinedData, searchQuery);
  }, [combinedData, searchQuery]);

  const handleAnnouncementClick = (announcement) => {
    setSelectedAnnouncement(announcement);
    setIsPanelOpen(true);
  };

  const handleClosePanel = () => {
    const isUnread = selectedAnnouncement && !selectedAnnouncement.seenBy?.some(seen => seen.userId === userId);
    setIsPanelOpen(false);

    if (isUnread) {
      setIsLoading(true);
      setLoadingMessage("Processing your request...");

      setTimeout(() => {
        setLoadingMessage("Announcements updated successfully! Checkmark");
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      }, 1500);
    }
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
  };

  const isUnread = (announcement) => {
    if (!userId) return false;
    return !announcement.seenBy?.some(seen => seen.userId === userId);
  };

  const totalPages = Math.ceil(filteredData.length / rowsPerPage);
  const paginatedData = filteredData.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= totalPages) {
      setCurrentPage(pageNumber);
      window.scrollTo(0, 0);
    }
  };

  return (
    <div className='container-fluid jk-announcement-container'>
      <div>
        <h1 className="jk-announcement-header">
          All Announcements
        </h1>

        <div className="jk-announcement-controls">
          <div className="jk-search-container">
            <div className="jk-search-input-wrapper">
              <BsSearch className="jk-search-icon" />
              <Form onSubmit={handleSearchSubmit}>
                <Form.Control
                  type="search"
                  placeholder="Search title, description, author..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  className="jk-announcement-search"
                />
              </Form>
            </div>
          </div>
        </div>

        <div className="jk-announcement-list">
          {paginatedData.length > 0 ? (
            paginatedData.map((item) => (
              <AnnouncementItem
                key={`${item.type}-${item._id}`}
                announcement={item}
                onClick={handleAnnouncementClick}
                isUnread={isUnread(item)}
              />
            ))
          ) : (
            <div className="jk-no-announcements">
              <TfiAnnouncement />
              <p>No announcements found</p>
            </div>
          )}
        </div>

        {filteredData.length > 0 && (
          <div className="jk-announcement-pagination">
            <div className="jk-pagination-info">
              Showing {(currentPage - 1) * rowsPerPage + 1} to {Math.min(currentPage * rowsPerPage, filteredData.length)} of {filteredData.length} entries
            </div>

            <div className="jk-pagination-controls">
              <Button
                variant="outline-primary"
                disabled={currentPage === 1}
                onClick={() => handlePageChange(currentPage - 1)}
                className="d-flex align-items-center"
              >
                <BsChevronLeft className="me-1" /> Previous
              </Button>

              {[...Array(Math.min(totalPages, 5))].map((_, index) => {
                const pageNum = index + Math.max(0, currentPage - 3);
                if (pageNum >= totalPages) return null;
                return (
                  <Button
                    key={pageNum}
                    variant={currentPage === pageNum + 1 ? "primary" : "outline-primary"}
                    onClick={() => handlePageChange(pageNum + 1)}
                    className="jk-page-btn"
                  >
                    {pageNum + 1}
                  </Button>
                );
              })}

              <Button
                variant="outline-primary"
                disabled={currentPage === totalPages}
                onClick={() => handlePageChange(currentPage + 1)}
                className="d-flex align-items-center"
              >
                Next <BsChevronRight className="ms-1" />
              </Button>
            </div>
          </div>
        )}
      </div>

      <LoadingAnimation isVisible={isLoading} message={loadingMessage} />

      <AnnouncementDetailPanel
        announcement={selectedAnnouncement}
        isOpen={isPanelOpen}
        onClose={handleClosePanel}
        userId={userId}
        isUnread={selectedAnnouncement && isUnread(selectedAnnouncement)}
      />

      {isPanelOpen && <div className="jk-panel-overlay" onClick={handleClosePanel}></div>}
    </div>
  );
}